import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const heroRef = useRef(null);
  const servicesRef = useRef(null);
  const ctaRef = useRef(null);

  useEffect(() => {
    // Animation sobre du hero au chargement
    const tl = gsap.timeline();
    
    tl.fromTo(heroRef.current.querySelector('h1'),
      { opacity: 0 },
      { opacity: 1, duration: 1, ease: "power2.out" }
    )
    .fromTo(heroRef.current.querySelector('.lead'),
      { opacity: 0 },
      { opacity: 1, duration: 0.8, ease: "power2.out" },
      "-=0.6"
    )
    .fromTo(heroRef.current.querySelectorAll('.btn'),
      { opacity: 0 },
      { opacity: 1, duration: 0.6, stagger: 0.2, ease: "power2.out" },
      "-=0.4"
    );

    // Animation sobre des services
    gsap.fromTo(servicesRef.current.querySelectorAll('.service-preview'),
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: servicesRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    // Animation sobre du CTA final
    gsap.fromTo(ctaRef.current,
      { opacity: 0 },
      {
        opacity: 1,
        duration: 0.8,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ctaRef.current,
          start: "top 80%",
          end: "bottom 20%",
          toggleActions: "play none none reverse"
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  const services = [
    {
      icon: '🐕',
      title: 'Bilan Comportemental',
      description: 'Diagnostic complet pour comprendre votre chien'
    },
    {
      icon: '🎓',
      title: 'Éducation Canine',
      description: 'Méthodes modernes et respectueuses'
    },
    {
      icon: '🔄',
      title: 'Rééducation',
      description: 'Correction des troubles comportementaux'
    }
  ];

  const stats = [
    { number: '10+', label: 'Années d\'expérience' },
    { number: '500+', label: 'Chiens accompagnés' },
    { number: '95%', label: 'Clients satisfaits' },
    { number: '24h', label: 'Délai de réponse' }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section ref={heroRef} className="hero interactive-particles">
        <div className="container">
          <div className="hero-content animate-fade-scale">
            <h1 className="neon-pulse">Atout Canin</h1>
            <p className="lead animate-slide-up">
              Éducation et rééducation canine professionnelle<br />
              Une approche respectueuse pour votre compagnon
            </p>
            <div className="hero-buttons">
              <Link to="/services" className="btn btn-secondary btn-large animate-bounce energy-field">
                Découvrir mes services
              </Link>
              <Link to="/contact" className="btn btn-outline btn-large animate-float crystal">
                Devis gratuit
              </Link>
            </div>
          </div>
        </div>
      </section>

{/*      
      <section ref={statsRef} className="section stats-section vortex">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item particles animate-fade-scale morph-3d">
                <h3 className="animate-sparkle">{stat.number}</h3>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
 */}
      {/* Services Aperçu */}
      <section ref={servicesRef} className="section interactive-particles">
        <div className="container">
          <div className="section-header">
            <h2 className="neon-secondary">Mes Services Principaux</h2>
            <p className="lead">
              Découvrez ma gamme complète de services d'éducation et de rééducation canine
            </p>
          </div>
          
          <div className="grid grid-3">
            {services.map((service, index) => (
              <div key={index} className="service-preview card animate-fade-scale depth-layer morph-advanced particles crystal">
                <div className="service-icon animate-float animate-fire" style={{ fontSize: '2.5rem' }}>
                  {service.icon}
                </div>
                <h3 className="neon-secondary animate-magnetic">{service.title}</h3>
                <p>{service.description}</p>
                <Link to="/services" className="btn btn-outline liquid plasma">
                  En savoir plus
                </Link>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-xl">
            <Link to="/services" className="btn btn-primary btn-large rainbow-neon quantum-leap">
              Voir tous mes services
            </Link>
          </div>
        </div>
      </section>

      {/* À Propos Aperçu */}
      <section className="section section-alt hologram">
        <div className="container">
          <div className="about-content">
            <div>
              <h2 className="neon-primary">Pourquoi choisir Atout Canin ?</h2>
              <p className="lead">
                Mon approche se base sur la compréhension du comportement canin et le renforcement positif.
              </p>
              <ul style={{ listStyle: 'none', padding: 0 }}>
                <li style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }} className="animate-slide-up">
                  <span style={{ fontSize: '1.5rem', marginRight: 'var(--spacing-sm)', color: 'var(--secondary-color)' }} className="animate-sparkle">✓</span>
                  Méthodes respectueuses et bienveillantes
                </li>
                <li style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }} className="animate-slide-up">
                  <span style={{ fontSize: '1.5rem', marginRight: 'var(--spacing-sm)', color: 'var(--secondary-color)' }} className="animate-sparkle">✓</span>
                  Approche personnalisée pour chaque chien
                </li>
                <li style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }} className="animate-slide-up">
                  <span style={{ fontSize: '1.5rem', marginRight: 'var(--spacing-sm)', color: 'var(--secondary-color)' }} className="animate-sparkle">✓</span>
                  Suivi et conseils adaptés
                </li>
                <li style={{ display: 'flex', alignItems: 'center', marginBottom: 'var(--spacing-sm)' }} className="animate-slide-up">
                  <span style={{ fontSize: '1.5rem', marginRight: 'var(--spacing-sm)', color: 'var(--secondary-color)' }} className="animate-sparkle">✓</span>
                  Formation continue et expertise
                </li>
              </ul>
              <Link to="/a-propos" className="btn btn-primary animate-electric">
                En savoir plus
              </Link>
            </div>
            <div className="about-image morph-3d">
              <img src="/1.png" alt="Atout Canin - Éducation canine" />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section ref={ctaRef} className="section energy-field">
        <div className="container">
          <div className="text-center">
            <h2 className="neon-secondary">Prêt à commencer l'éducation de votre chien ?</h2>
            <p className="lead">
              Contactez-moi pour un bilan personnalisé et découvrez comment je peux aider votre compagnon
            </p>
            <Link to="/contact" className="btn btn-secondary btn-large rainbow-neon quantum-leap">
              Obtenir un devis gratuit
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 Atout Canin - Éducation et Rééducation Canine. Tous droits réservés.</p>
          <p>
            <Link to="/cgv">Conditions Générales de Vente</Link>
            <Link to="/contact">Mentions Légales</Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Home; 